import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Calculator, DollarSign, Info, TrendingUp, PieChart as PieChartIcon } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';

const STORAGE_KEY = 'dividend_data';

export const DividendCalculator = () => {
  // State for inputs
  const [shares, setShares] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).shares : 100;
  });
  
  const [dividendPerShare, setDividendPerShare] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).dividendPerShare : 2.5;
  });
  
  const [frequency, setFrequency] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).frequency : 'quarterly';
  });
  
  const [sharePrice, setSharePrice] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).sharePrice : 50;
  });
  
  const [years, setYears] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).years : 5;
  });

  const [reinvest, setReinvest] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).reinvest : false;
  });

  // Save to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      shares,
      dividendPerShare,
      frequency,
      sharePrice,
      years,
      reinvest
    }));
  }, [shares, dividendPerShare, frequency, sharePrice, years, reinvest]);

  const frequencyMap = {
    'monthly': { n: 12, label: 'Monthly' },
    'quarterly': { n: 4, label: 'Quarterly' },
    'semi-annual': { n: 2, label: 'Semi-Annual' },
    'annual': { n: 1, label: 'Annual' }
  };

  // Calculate dividend income
  const calculateDividend = () => {
    const numShares = parseFloat(shares) || 0;
    const divPerShare = parseFloat(dividendPerShare) || 0;
    const price = parseFloat(sharePrice) || 0;
    const n = frequencyMap[frequency].n;
    const t = parseFloat(years) || 0;

    const annualDividend = numShares * divPerShare * n;
    const monthlyIncome = annualDividend / 12;
    const totalIncome = annualDividend * t;
    const dividendYield = price > 0 ? (divPerShare * n / price) * 100 : 0;
    const portfolioValue = numShares * price;

    return {
      annualDividend,
      monthlyIncome,
      totalIncome,
      dividendYield,
      portfolioValue
    };
  };

  // Generate year-wise income data
  const generateYearWiseData = () => {
    const numShares = parseFloat(shares) || 0;
    const divPerShare = parseFloat(dividendPerShare) || 0;
    const n = frequencyMap[frequency].n;
    const t = parseFloat(years) || 0;
    const price = parseFloat(sharePrice) || 0;

    const data = [];
    let currentShares = numShares;
    let totalDividends = 0;

    for (let year = 0; year <= t; year++) {
      const annualDividend = year === 0 ? 0 : currentShares * divPerShare * n;
      totalDividends += annualDividend;

      data.push({
        year: year,
        income: Math.round(annualDividend),
        cumulativeIncome: Math.round(totalDividends),
        shares: Math.round(currentShares)
      });

      // Reinvest logic
      if (reinvest && year < t && price > 0) {
        const newShares = annualDividend / price;
        currentShares += newShares;
      }
    }
    return data;
  };

  // Compare reinvest vs no reinvest
  const compareReinvestment = () => {
    const numShares = parseFloat(shares) || 0;
    const divPerShare = parseFloat(dividendPerShare) || 0;
    const n = frequencyMap[frequency].n;
    const t = parseFloat(years) || 0;
    const price = parseFloat(sharePrice) || 0;

    const data = [];
    let reinvestedShares = numShares;
    let totalReinvested = 0;
    let totalNonReinvested = 0;

    for (let year = 0; year <= t; year++) {
      const nonReinvestedIncome = year === 0 ? 0 : numShares * divPerShare * n;
      const reinvestedIncome = year === 0 ? 0 : reinvestedShares * divPerShare * n;
      
      totalNonReinvested += nonReinvestedIncome;
      totalReinvested += reinvestedIncome;

      data.push({
        year: year,
        withoutReinvestment: Math.round(totalNonReinvested),
        withReinvestment: Math.round(totalReinvested)
      });

      // Reinvest for next year
      if (year < t && price > 0) {
        const newShares = reinvestedIncome / price;
        reinvestedShares += newShares;
      }
    }
    return data;
  };

  // Dividend breakdown by period
  const getDividendBreakdown = () => {
    const annualDividend = calculateDividend().annualDividend;
    const n = frequencyMap[frequency].n;
    const perPayment = annualDividend / n;

    return [
      { name: 'Per Payment', value: Math.round(perPayment) },
      { name: 'Quarterly (4x)', value: Math.round(annualDividend / 4) },
      { name: 'Monthly (12x)', value: Math.round(annualDividend / 12) },
      { name: 'Annual', value: Math.round(annualDividend) }
    ];
  };

  const results = calculateDividend();
  const yearWiseData = generateYearWiseData();
  const reinvestComparison = compareReinvestment();
  const dividendBreakdown = getDividendBreakdown();

  const COLORS = [
    'hsl(var(--chart-1))',
    'hsl(var(--chart-2))',
    'hsl(var(--chart-3))',
    'hsl(var(--chart-4))'
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      {/* Navigation */}
      <nav className="border-b border-border/40 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <Separator orientation="vertical" className="h-6" />
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg gradient-secondary flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-white" />
                </div>
                <span className="font-display text-lg font-semibold text-foreground">
                  Dividend Calculator
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="calculator" className="space-y-6">
          {/* Horizontal Scrollable Tabs */}
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
            <TabsList className="inline-flex w-auto min-w-full sm:min-w-0 bg-muted/50 p-1 h-auto">
              <TabsTrigger 
                value="calculator" 
                className="data-[state=active]:bg-[hsl(var(--secondary))] data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Calculator & Results
              </TabsTrigger>
              <TabsTrigger 
                value="charts" 
                className="data-[state=active]:bg-[hsl(var(--secondary))] data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Visual Analysis
              </TabsTrigger>
              <TabsTrigger 
                value="insights" 
                className="data-[state=active]:bg-[hsl(var(--secondary))] data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Info className="w-4 h-4 mr-2" />
                Insights & Explanation
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Tab 1: Calculator Overview & Results */}
          <TabsContent value="calculator" className="space-y-6 animate-fade-in">
            {/* Introduction */}
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">What is Dividend Income?</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  Dividend income is passive income generated from owning dividend-paying stocks. Companies distribute 
                  a portion of their profits to shareholders regularly. This calculator helps you understand your potential 
                  passive income stream, dividend yield, and the powerful impact of dividend reinvestment over time.
                </CardDescription>
              </CardHeader>
            </Card>

            <div className="grid lg:grid-cols-3 gap-6">
              {/* Input Section */}
              <Card className="lg:col-span-1 border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Input Parameters</CardTitle>
                  <CardDescription>Enter your portfolio details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="shares" className="text-sm font-medium">
                      Number of Shares
                    </Label>
                    <Input
                      id="shares"
                      type="number"
                      value={shares}
                      onChange={(e) => setShares(e.target.value)}
                      placeholder="100"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="dividendPerShare" className="text-sm font-medium">
                      Dividend Per Share ($)
                    </Label>
                    <Input
                      id="dividendPerShare"
                      type="number"
                      value={dividendPerShare}
                      onChange={(e) => setDividendPerShare(e.target.value)}
                      placeholder="2.5"
                      step="0.1"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="frequency" className="text-sm font-medium">
                      Dividend Frequency
                    </Label>
                    <Select value={frequency} onValueChange={setFrequency}>
                      <SelectTrigger id="frequency" className="text-base">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.keys(frequencyMap).map(key => (
                          <SelectItem key={key} value={key}>
                            {frequencyMap[key].label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="sharePrice" className="text-sm font-medium">
                      Current Share Price ($)
                    </Label>
                    <Input
                      id="sharePrice"
                      type="number"
                      value={sharePrice}
                      onChange={(e) => setSharePrice(e.target.value)}
                      placeholder="50"
                      step="0.01"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="years" className="text-sm font-medium">
                      Time Period (Years)
                    </Label>
                    <Input
                      id="years"
                      type="number"
                      value={years}
                      onChange={(e) => setYears(e.target.value)}
                      placeholder="5"
                      className="text-base"
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="reinvest"
                      checked={reinvest}
                      onChange={(e) => setReinvest(e.target.checked)}
                      className="w-4 h-4 rounded border-border"
                    />
                    <Label htmlFor="reinvest" className="text-sm font-medium cursor-pointer">
                      Reinvest Dividends
                    </Label>
                  </div>

                  <Button 
                    className="w-full gradient-secondary hover:opacity-90 transition-opacity shadow-secondary"
                    onClick={() => setShares(shares)}
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    Calculate
                  </Button>
                </CardContent>
              </Card>

              {/* Results Section */}
              <div className="lg:col-span-2 space-y-6">
                <Card className="border-border/50 shadow-md">
                  <CardHeader>
                    <CardTitle className="font-display">Dividend Income Results</CardTitle>
                    <CardDescription>Your passive income summary</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="p-6 rounded-xl bg-gradient-to-br from-[hsl(var(--secondary-light))] to-white border border-[hsl(var(--secondary))]/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-[hsl(var(--secondary))] flex items-center justify-center">
                            <DollarSign className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Annual Dividend Income</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-[hsl(var(--secondary))]">
                          {formatCurrency(results.annualDividend)}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-[hsl(var(--accent-light))] to-white border border-[hsl(var(--accent))]/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-[hsl(var(--accent))] flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Monthly Income Equivalent</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-[hsl(var(--accent))]">
                          {formatCurrency(results.monthlyIncome)}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-[hsl(var(--primary-light))] to-white border border-[hsl(var(--primary))]/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-[hsl(var(--primary))] flex items-center justify-center">
                            <PieChartIcon className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Dividend Yield</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-[hsl(var(--primary))]">
                          {results.dividendYield.toFixed(2)}%
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-[hsl(var(--purple-light))] to-white border border-[hsl(var(--purple))]/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-[hsl(var(--purple))] flex items-center justify-center">
                            <Calculator className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Total Income Over {years} Years</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-[hsl(var(--purple))]">
                          {formatCurrency(results.totalIncome)}
                        </p>
                      </div>
                    </div>

                    <Separator className="my-6" />

                    <div className="p-6 rounded-xl bg-gradient-chart">
                      <p className="text-sm font-medium text-muted-foreground mb-2">Portfolio Value</p>
                      <p className="text-4xl font-display font-bold text-foreground mb-2">
                        {formatCurrency(results.portfolioValue)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Based on {shares} shares at {formatCurrency(parseFloat(sharePrice))} per share
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Year-wise Income Table */}
                <Card className="border-border/50 shadow-md">
                  <CardHeader>
                    <CardTitle className="font-display">Year-wise Dividend Income</CardTitle>
                    <CardDescription>
                      {reinvest ? 'With dividend reinvestment - watch your shares grow!' : 'Annual dividend payments breakdown'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-3 px-4 font-semibold text-foreground">Year</th>
                            {reinvest && <th className="text-right py-3 px-4 font-semibold text-foreground">Shares Owned</th>}
                            <th className="text-right py-3 px-4 font-semibold text-foreground">Annual Income</th>
                            <th className="text-right py-3 px-4 font-semibold text-foreground">Cumulative Income</th>
                          </tr>
                        </thead>
                        <tbody>
                          {yearWiseData.map((row, index) => (
                            <tr 
                              key={index} 
                              className="border-b border-border/50 hover:bg-muted/30 transition-colors"
                            >
                              <td className="py-3 px-4 font-medium">{row.year}</td>
                              {reinvest && <td className="py-3 px-4 text-right text-muted-foreground">{row.shares}</td>}
                              <td className="py-3 px-4 text-right text-[hsl(var(--secondary))]">{formatCurrency(row.income)}</td>
                              <td className="py-3 px-4 text-right font-semibold text-[hsl(var(--primary))]">{formatCurrency(row.cumulativeIncome)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Tab 2: Visual Analysis (Charts) */}
          <TabsContent value="charts" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Annual Dividend Income Over Time</CardTitle>
                <CardDescription className="text-base">
                  Track your yearly passive income growth
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={yearWiseData}>
                      <defs>
                        <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(var(--secondary))" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="hsl(var(--secondary))" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="year" 
                        label={{ value: 'Year', position: 'insideBottom', offset: -5 }}
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <YAxis 
                        label={{ value: 'Annual Income ($)', angle: -90, position: 'insideLeft' }}
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => `$${(value / 1000).toFixed(1)}k`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="income" 
                        stroke="hsl(var(--secondary))" 
                        fill="url(#colorIncome)"
                        strokeWidth={3}
                        name="Annual Dividend Income"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Cumulative Income Growth</CardTitle>
                  <CardDescription>
                    Total dividends earned over time
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={yearWiseData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="year" 
                          stroke="hsl(var(--muted-foreground))"
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="cumulativeIncome" 
                          stroke="hsl(var(--primary))" 
                          strokeWidth={3}
                          name="Total Income"
                          dot={{ fill: 'hsl(var(--primary))' }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Dividend Payment Breakdown</CardTitle>
                  <CardDescription>
                    Income frequency comparison
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={dividendBreakdown}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="name" 
                          stroke="hsl(var(--muted-foreground))"
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${value}`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Bar dataKey="value" name="Income Amount">
                          {dividendBreakdown.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="font-display">Reinvested vs Non-Reinvested Dividends</CardTitle>
                <CardDescription className="text-base">
                  See the dramatic difference dividend reinvestment makes over time
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={reinvestComparison}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="year" 
                        label={{ value: 'Year', position: 'insideBottom', offset: -5 }}
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <YAxis 
                        label={{ value: 'Cumulative Income ($)', angle: -90, position: 'insideLeft' }}
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="withReinvestment" 
                        stroke="hsl(var(--accent))" 
                        strokeWidth={3}
                        name="With Reinvestment"
                        dot={false}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="withoutReinvestment" 
                        stroke="hsl(var(--muted-foreground))" 
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        name="Without Reinvestment"
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab 3: Explanation, Risks & Insights */}
          <TabsContent value="insights" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Understanding Your Dividend Income</CardTitle>
                <CardDescription className="text-base">
                  Learn what these numbers mean and how to maximize your passive income strategy
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-[hsl(var(--secondary))]">
                    What is Dividend Yield?
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-3">
                    Dividend yield shows the annual return you're getting from dividends as a percentage of the stock price. 
                    Your current dividend yield is <strong className="text-[hsl(var(--secondary))]">{results.dividendYield.toFixed(2)}%</strong>.
                  </p>
                  <div className="p-4 rounded-lg bg-muted/50 text-sm">
                    <p className="font-mono mb-2">Dividend Yield = (Annual Dividend Per Share / Share Price) × 100</p>
                    <p className="font-mono">
                      Dividend Yield = ({formatCurrency(parseFloat(dividendPerShare) * frequencyMap[frequency].n)} / {formatCurrency(parseFloat(sharePrice))}) × 100 = {results.dividendYield.toFixed(2)}%
                    </p>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-[hsl(var(--accent))]">
                    Your Dividend Income Breakdown
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-[hsl(var(--secondary-light))] border border-[hsl(var(--secondary))]/20">
                      <p className="text-sm font-medium text-muted-foreground mb-1">Annual Income</p>
                      <p className="text-2xl font-bold text-[hsl(var(--secondary))] mb-2">
                        {formatCurrency(results.annualDividend)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {shares} shares × {formatCurrency(parseFloat(dividendPerShare))} × {frequencyMap[frequency].n} payments/year
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-[hsl(var(--accent-light))] border border-[hsl(var(--accent))]/20">
                      <p className="text-sm font-medium text-muted-foreground mb-1">Monthly Equivalent</p>
                      <p className="text-2xl font-bold text-[hsl(var(--accent))] mb-2">
                        {formatCurrency(results.monthlyIncome)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Steady passive income every month
                      </p>
                    </div>
                  </div>
                  <p className="text-base text-muted-foreground leading-relaxed mt-4">
                    Over {years} years, you'll receive a total of <strong className="text-[hsl(var(--primary))]">
                      {formatCurrency(reinvest ? reinvestComparison[reinvestComparison.length - 1].withReinvestment : results.totalIncome)}
                    </strong> in dividend payments{reinvest ? ' (with reinvestment)' : ''}.
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-[hsl(var(--primary))]">
                    Dividend Yield vs Capital Gains: The Trade-off
                  </h3>
                  <div className="space-y-4 text-base text-muted-foreground leading-relaxed">
                    <div className="p-4 rounded-lg bg-muted/50">
                      <h4 className="font-semibold text-foreground mb-2">📊 High Dividend Yield Stocks</h4>
                      <p className="mb-2">
                        <strong>Pros:</strong> Steady, predictable income; less volatility; great for income-focused investors
                      </p>
                      <p>
                        <strong>Cons:</strong> Typically lower capital appreciation; may indicate slower company growth
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-muted/50">
                      <h4 className="font-semibold text-foreground mb-2">📈 Growth Stocks (Low/No Dividends)</h4>
                      <p className="mb-2">
                        <strong>Pros:</strong> Higher potential for capital gains; reinvested profits fuel company growth
                      </p>
                      <p>
                        <strong>Cons:</strong> No regular income; more volatile; returns depend on selling stock
                      </p>
                    </div>
                    <p className="mt-4">
                      Your portfolio's dividend yield of <strong>{results.dividendYield.toFixed(2)}%</strong> suggests {
                        results.dividendYield > 4 ? 'a strong income focus - excellent for passive income but watch company fundamentals' :
                        results.dividendYield > 2 ? 'a balanced approach between income and growth potential' :
                        'a growth-oriented strategy with modest dividend income'
                      }.
                    </p>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-[hsl(var(--purple))]">
                    The Power of Dividend Reinvestment (DRIP)
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    When you reinvest dividends, you use them to buy more shares, which then generate even more dividends. 
                    This creates a compounding effect similar to compound interest.
                  </p>
                  <div className="p-6 rounded-xl bg-gradient-to-br from-[hsl(var(--accent-light))] to-white border border-[hsl(var(--accent))]/20">
                    <p className="text-sm font-medium text-muted-foreground mb-2">Impact Over {years} Years</p>
                    <div className="grid sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-lg font-semibold text-foreground">Without Reinvestment</p>
                        <p className="text-3xl font-display font-bold text-[hsl(var(--muted-foreground))]">
                          {formatCurrency(reinvestComparison[reinvestComparison.length - 1].withoutReinvestment)}
                        </p>
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-foreground">With Reinvestment</p>
                        <p className="text-3xl font-display font-bold text-[hsl(var(--accent))]">
                          {formatCurrency(reinvestComparison[reinvestComparison.length - 1].withReinvestment)}
                        </p>
                      </div>
                    </div>
                    <p className="text-base font-semibold text-[hsl(var(--accent))]">
                      Extra income from reinvestment: {formatCurrency(
                        reinvestComparison[reinvestComparison.length - 1].withReinvestment - 
                        reinvestComparison[reinvestComparison.length - 1].withoutReinvestment
                      )}
                    </p>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-[hsl(var(--destructive))]">
                    Important Risks and Considerations
                  </h3>
                  <div className="space-y-3 text-base text-muted-foreground leading-relaxed">
                    <div className="flex gap-3">
                      <span className="text-[hsl(var(--destructive))] font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Dividend Cuts:</strong> Companies can reduce or eliminate 
                        dividends during financial difficulties. This calculator assumes constant dividend payments.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-[hsl(var(--destructive))] font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Share Price Volatility:</strong> While you earn dividends, 
                        the share price can fluctuate. A falling stock price can offset dividend gains.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-[hsl(var(--destructive))] font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Tax Implications:</strong> Dividend income is taxable. 
                        Qualified dividends typically receive favorable tax treatment, but rates vary.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-[hsl(var(--destructive))] font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Concentration Risk:</strong> Relying on dividends from 
                        a single stock or sector increases risk. Diversification is crucial.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-[hsl(var(--destructive))] font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Inflation Impact:</strong> Your dividend income needs to 
                        grow faster than inflation to maintain purchasing power over time.
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-[hsl(var(--accent))]">
                    Practical Strategies for Dividend Investors
                  </h3>
                  <div className="space-y-4 text-base">
                    <div className="p-4 rounded-lg bg-[hsl(var(--primary-light))] border-l-4 border-[hsl(var(--primary))]">
                      <h4 className="font-semibold text-foreground mb-2">✅ Focus on Dividend Aristocrats</h4>
                      <p className="text-muted-foreground">
                        Companies that have increased dividends for 25+ consecutive years show commitment to shareholders 
                        and financial stability.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-[hsl(var(--accent-light))] border-l-4 border-[hsl(var(--accent))]">
                      <h4 className="font-semibold text-foreground mb-2">✅ Reinvest During Accumulation Phase</h4>
                      <p className="text-muted-foreground">
                        If you don't need the income now, reinvesting dividends accelerates wealth building through compounding.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-[hsl(var(--secondary-light))] border-l-4 border-[hsl(var(--secondary))]">
                      <h4 className="font-semibold text-foreground mb-2">✅ Check Payout Ratio</h4>
                      <p className="text-muted-foreground">
                        A sustainable payout ratio (40-60% of earnings) indicates dividends are safe and room exists for growth.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-[hsl(var(--purple-light))] border-l-4 border-[hsl(var(--purple))]">
                      <h4 className="font-semibold text-foreground mb-2">✅ Diversify Across Sectors</h4>
                      <p className="text-muted-foreground">
                        Spread dividend investments across different industries to reduce risk from sector-specific downturns.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default DividendCalculator;
